// ============================================================================
// campaign.js — the Ashen Host's war against the Magmar Dynasty, 14 battles over
// three acts. Each level parameterises the battlefield: its BIOME (the land it is
// fought on — ashlands, emberwood, frostmark, saltmere, goldfen, ironpeak,
// duskmarsh), a KIND tag (rout/siege/defense/ambush/duel/laststand/escort/gauntlet),
// difficulty mods, which bastions the Magmar hold, the enemy warlords / Dynast and
// allied generals, the win objectives + fail conditions, the briefing/outcome
// dialogue, and a SCRIPT of mid-battle cinematic beats (reinforcements, ambushes,
// boss entrances, dialogue barks, minimap pings) run by game/levelScript.js.
// Difficulty climbs monotonically. Designed via a multi-agent design pass, then
// validated against the engine's real objective/fail/biome/roster vocabulary.
//
// Speakers in dialogue: BENJEN, HALDRIC, SERA, GARRICK, LYSA, BRAM, ISOLDE, CORAN,
// and MAGMAR (the enemy voice). `par` feeds the post-battle rank (S/A/B/C).
// ============================================================================

export const CAMPAIGN = [
  {
    "id": "l1",
    "act": "ACT I — THE ASHLANDS BURN",
    "name": "First Blood at Redmarch",
    "subtitle": "The Magmar cross the ash-flats at dawn. Blood them here.",
    "biome": "ashlands",
    "kind": "rout",
    "mods": {
      "enemyHp": 0.75,
      "enemyDmg": 0.72,
      "spawn": 0.8,
      "magmarNear": 160,
      "allyNear": 150
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "morale": 55,
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "rout",
        "count": 120
      }
    ],
    "fail": [],
    "par": {
      "kills": 120,
      "time": 150
    },
    "intro": [
      {
        "who": "HALDRIC",
        "line": "They crossed the fire-line at dawn — thousands. The Dynast means to burn the whole Ash-March to the root."
      },
      {
        "who": "BENJEN",
        "line": "Then we teach them what the black sword costs. Sound the host, Warmaster — I take the field."
      }
    ],
    "win": "The first host breaks on Redmarch. It will not be the last.",
    "lose": "Redmarch is overrun. The Ashlands burn one field closer to home.",
    "script": [
      {
        "t": 8,
        "say": {
          "who": "HALDRIC",
          "line": "Their front is thin. Break it before the second host lands."
        }
      },
      {
        "kills": 40,
        "say": {
          "who": "BENJEN",
          "line": "Forty in the ash. They bleed like any other men."
        }
      },
      {
        "t": 45,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 24,
          "at": "north"
        }
      },
      {
        "kills": 90,
        "announce": {
          "text": "THE MAGMAR LINE WAVERS",
          "tone": "good"
        }
      },
      {
        "kills": 110,
        "say": {
          "who": "HALDRIC",
          "line": "Ten more and they run. Finish it."
        }
      }
    ]
  },
  {
    "id": "l2",
    "act": "ACT I — THE ASHLANDS BURN",
    "name": "The Burning of Hearthfen",
    "subtitle": "The golden granges are alight. Reach the holdouts before the fire does.",
    "biome": "goldfen",
    "kind": "gauntlet",
    "mods": {
      "enemyHp": 0.82,
      "enemyDmg": 0.78,
      "spawn": 0.86,
      "magmarNear": 173,
      "allyNear": 147
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane",
      "Sera Volk"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "reach",
        "x": 0,
        "z": -110,
        "r": 16,
        "label": "Reach Hearthfen and relieve the holdouts"
      }
    ],
    "fail": [
      {
        "type": "timeout",
        "time": 150
      }
    ],
    "par": {
      "kills": 70,
      "time": 150
    },
    "intro": [
      {
        "who": "SERA",
        "line": "Hearthfen's granges are alight, Commander. The farmholders are penned in the last barn."
      },
      {
        "who": "BENJEN",
        "line": "Then we do not stop until we reach them. Every heartbeat is a life. Ride."
      }
    ],
    "win": "Hearthfen still stands — smoke-blind, but breathing. The golden fen is not yet ash.",
    "lose": "The last barn falls before we reach it. Hearthfen burns down to the roots.",
    "script": [
      {
        "t": 4,
        "objective": {
          "text": "Ride for Hearthfen — the farmholders cannot hold long."
        }
      },
      {
        "t": 4,
        "ping": {
          "at": "north",
          "tone": "bad"
        }
      },
      {
        "t": 40,
        "announce": {
          "text": "THE OUTER GRANGE FALLS",
          "tone": "bad"
        }
      },
      {
        "t": 42,
        "spawn": {
          "team": "magmar",
          "type": "archer",
          "count": 16,
          "at": "flank"
        }
      },
      {
        "t": 85,
        "say": {
          "who": "SERA",
          "line": "The village gate won't hold. Faster, Benjen — ride!"
        }
      },
      {
        "t": 120,
        "ping": {
          "at": "north",
          "tone": "neutral"
        }
      }
    ]
  },
  {
    "id": "l3",
    "act": "ACT I — THE ASHLANDS BURN",
    "name": "Banners in the Pines",
    "subtitle": "Two Magmar banners fly deep in the burnt wood. Pull them down.",
    "biome": "emberwood",
    "kind": "siege",
    "mods": {
      "enemyHp": 0.9,
      "enemyDmg": 0.84,
      "spawn": 0.92,
      "magmarNear": 186,
      "allyNear": 144
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "warlords": [
      "Vurmog"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Sera Volk"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "capture",
        "count": 2
      }
    ],
    "fail": [],
    "par": {
      "kills": 110,
      "time": 200
    },
    "intro": [
      {
        "who": "SERA",
        "line": "Two Magmar banners fly deep in the burnt pines. Vurmog crowns himself among them."
      },
      {
        "who": "BENJEN",
        "line": "Pull the banners down and the wood is ours. Let the Cinder-Crowned keep his crown — for now."
      }
    ],
    "win": "The pines fly the black and silver. Vurmog fled his own banners — we will meet again.",
    "lose": "The banners still burn red in the pines. The wood stays the Dynasty's.",
    "script": [
      {
        "t": 6,
        "say": {
          "who": "SERA",
          "line": "Two banners in the pines. Pull them down and the wood is ours."
        }
      },
      {
        "kills": 25,
        "say": {
          "who": "MAGMAR",
          "line": "Into the fire, ashling. Vurmog will crown his banner with your skull."
        }
      },
      {
        "t": 55,
        "spawn": {
          "team": "magmar",
          "type": "captain",
          "count": 2,
          "at": "around"
        }
      },
      {
        "t": 85,
        "say": {
          "who": "BENJEN",
          "line": "One banner down. Sera — take the second."
        }
      },
      {
        "t": 120,
        "announce": {
          "text": "THE PINES ARE BURNING",
          "tone": "neutral"
        }
      }
    ]
  },
  {
    "id": "l4",
    "act": "ACT I — THE ASHLANDS BURN",
    "name": "The Molten Oath",
    "subtitle": "Tholvar swore never to yield the bog. Hold the mire and end him.",
    "biome": "duskmarsh",
    "kind": "duel",
    "mods": {
      "enemyHp": 0.97,
      "enemyDmg": 0.9,
      "spawn": 0.98,
      "magmarNear": 199,
      "allyNear": 141
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "warlords": [
      "Tholvar"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Sera Volk"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "slayWarlord",
        "name": "Tholvar"
      },
      {
        "type": "survive",
        "time": 90
      }
    ],
    "fail": [],
    "par": {
      "kills": 130,
      "time": 190
    },
    "intro": [
      {
        "who": "HALDRIC",
        "line": "Tholvar of the Molten Oath holds the bog. He swore never to yield a step of ground."
      },
      {
        "who": "BENJEN",
        "line": "Every oath ends under Masamune. Hold the mire, break his guard — and the vow with it."
      }
    ],
    "win": "Tholvar's oath drowns in his own bog. The east is broken open.",
    "lose": "The Molten Oath holds. Tholvar stands over another sunken host.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "HALDRIC",
          "line": "Tholvar holds the mire with twin blades and a vow. Ninety seconds, then it is ours."
        }
      },
      {
        "t": 5,
        "ping": {
          "at": "center",
          "tone": "bad"
        }
      },
      {
        "kills": 20,
        "say": {
          "who": "MAGMAR",
          "line": "My oath is the mud you drown in. Come, black sword."
        }
      },
      {
        "t": 50,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 20,
          "at": "around"
        }
      },
      {
        "t": 90,
        "announce": {
          "text": "THE MIRE HOLDS",
          "tone": "good"
        }
      },
      {
        "t": 92,
        "say": {
          "who": "BENJEN",
          "line": "The line stands. Now the Molten Oath ends."
        }
      }
    ]
  },
  {
    "id": "l5",
    "act": "ACT I — THE ASHLANDS BURN",
    "name": "The Sea-Cave Snare",
    "subtitle": "The coast road was too quiet. Break the ring and cut down the Widow who set it.",
    "biome": "saltmere",
    "kind": "ambush",
    "mods": {
      "enemyHp": 1.04,
      "enemyDmg": 0.96,
      "spawn": 1.04,
      "magmarNear": 212,
      "allyNear": 138
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "start": {
      "x": 0,
      "z": 0,
      "heading": 0
    },
    "morale": 38,
    "warlords": [
      "Ysra Coalheart"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Old Garrick"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "morale",
        "value": 60
      },
      {
        "type": "rout",
        "count": 150
      },
      {
        "type": "slayWarlord",
        "name": "Ysra Coalheart"
      }
    ],
    "fail": [],
    "par": {
      "kills": 170,
      "time": 240
    },
    "intro": [
      {
        "who": "GARRICK",
        "line": "The coast road's too quiet, Commander. Old Garrick doesn't like quiet."
      },
      {
        "who": "BENJEN",
        "line": "Nor do I. Keep the men close, eyes on the sea-caves."
      },
      {
        "who": "MAGMAR",
        "line": "Too late for eyes, Daguth. The Widowing Flame already lit your pyre."
      }
    ],
    "win": "The sea-cave snare is sprung and broken, and Ysra Coalheart burns cold on the salt. The coast is ours.",
    "lose": "The trap closes. The surf runs red, and the Widowing Flame laughs from the rocks.",
    "script": [
      {
        "t": 2,
        "announce": {
          "text": "AMBUSH — THE HOST IS SURROUNDED",
          "tone": "bad"
        }
      },
      {
        "t": 2,
        "say": {
          "who": "GARRICK",
          "line": "They came out of the sea-caves! Back to back, all of you!"
        }
      },
      {
        "t": 4,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 30,
          "at": "around"
        }
      },
      {
        "t": 26,
        "say": {
          "who": "MAGMAR",
          "line": "I make widows, Daguth. Today I make one of your whole host."
        }
      },
      {
        "kills": 70,
        "say": {
          "who": "BENJEN",
          "line": "The tide turns. Break their ring — drive them into the surf."
        }
      },
      {
        "t": 105,
        "spawn": {
          "team": "magmar",
          "type": "archer",
          "count": 18,
          "at": "flank"
        }
      },
      {
        "kills": 130,
        "announce": {
          "text": "THE SNARE COLLAPSES",
          "tone": "good"
        }
      }
    ]
  },
  {
    "id": "l6",
    "act": "ACT II — THE MARCHES OF FIRE",
    "name": "The Frostmark Pass",
    "subtitle": "Two holds in the snow are yours. Lose neither, or the Marches open.",
    "biome": "frostmark",
    "kind": "defense",
    "mods": {
      "enemyHp": 1.11,
      "enemyDmg": 1.02,
      "spawn": 1.1,
      "magmarNear": 225,
      "allyNear": 135
    },
    "bastionsMagmar": [
      0,
      1
    ],
    "morale": 44,
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane",
      "Old Garrick",
      "Lysa Fenn"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "survive",
        "time": 180
      }
    ],
    "fail": [
      {
        "type": "holdBastions",
        "ids": [
          2,
          3
        ]
      }
    ],
    "par": {
      "kills": 200,
      "time": 200
    },
    "intro": [
      {
        "who": "LYSA",
        "line": "The Frostmark pass is ours — two holds in the snow. Lose either and the Marches open behind us."
      },
      {
        "who": "BENJEN",
        "line": "Then we lose neither. Three minutes against the cold and the horde. Hold."
      }
    ],
    "win": "The pass holds. Both banners stand black against the snow. The Marches stay shut.",
    "lose": "A hold falls in the white. The Frostmark opens, and the fire pours through.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "LYSA",
          "line": "Two holds and the snow against us. Keep them both, Commander."
        }
      },
      {
        "t": 20,
        "ping": {
          "at": "north",
          "tone": "bad"
        }
      },
      {
        "t": 30,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 26,
          "at": "north"
        }
      },
      {
        "t": 95,
        "say": {
          "who": "LYSA",
          "line": "Ashgate's wall is breaking — I need swords here, now!"
        }
      },
      {
        "t": 100,
        "announce": {
          "text": "ASHGATE KEEP UNDER ASSAULT",
          "tone": "bad"
        }
      },
      {
        "t": 150,
        "spawn": {
          "team": "magmar",
          "type": "captain",
          "count": 3,
          "at": "around"
        }
      }
    ]
  },
  {
    "id": "l7",
    "act": "ACT II — THE MARCHES OF FIRE",
    "name": "The Ashen Arrow",
    "subtitle": "Lysa Fenn is cut off on the slate — and the Warden is hunting her. Carve her a road.",
    "biome": "ironpeak",
    "kind": "escort",
    "mods": {
      "enemyHp": 1.18,
      "enemyDmg": 1.08,
      "spawn": 1.16,
      "magmarNear": 238,
      "allyNear": 132
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "start": {
      "x": 0,
      "z": 40,
      "heading": 3.14159
    },
    "morale": 46,
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane",
      "Lysa Fenn"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "reach",
        "x": 0,
        "z": -120,
        "r": 16,
        "label": "Lead Lysa to the pass-head"
      }
    ],
    "fail": [
      {
        "type": "protect",
        "name": "Lysa Fenn"
      }
    ],
    "par": {
      "kills": 150,
      "time": 220
    },
    "intro": [
      {
        "who": "LYSA",
        "line": "My company's scattered across the slate, and the Magmar between me and the pass. I need a road, Commander."
      },
      {
        "who": "BENJEN",
        "line": "Then I'll carve you one. Stay at my shoulder, Ashen Arrow — I'll not lose you on this rock."
      }
    ],
    "win": "Lysa Fenn reaches the pass-head, quiver empty, still breathing. The highlands are crossed.",
    "lose": "The Ashen Arrow falls on the slate. The pass takes another of the Host's best.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "LYSA",
          "line": "My company's scattered on the slate. I'll not make the pass alone."
        }
      },
      {
        "t": 6,
        "ping": {
          "at": "north",
          "tone": "neutral"
        }
      },
      {
        "t": 35,
        "spawn": {
          "team": "magmar",
          "type": "archer",
          "count": 18,
          "at": "flank"
        }
      },
      {
        "t": 45,
        "warlord": "Draxa Embermaw",
        "say": {
          "who": "MAGMAR",
          "line": "The Warden of Slag smells a wounded Arrow. Cut her down before she reaches the pass."
        }
      },
      {
        "t": 80,
        "say": {
          "who": "LYSA",
          "line": "Keep the Warden off my back and I'll match your stride."
        }
      },
      {
        "t": 115,
        "announce": {
          "text": "THE PASS-HEAD IS IN SIGHT",
          "tone": "good"
        }
      }
    ]
  },
  {
    "id": "l8",
    "act": "ACT II — THE MARCHES OF FIRE",
    "name": "The Two That Fled",
    "subtitle": "The two lords who slipped your grip have rallied on the fen. End them both.",
    "biome": "goldfen",
    "kind": "rout",
    "mods": {
      "enemyHp": 1.25,
      "enemyDmg": 1.14,
      "spawn": 1.22,
      "magmarNear": 251,
      "allyNear": 129
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "warlords": [
      "Vurmog",
      "Draxa Embermaw"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Old Garrick",
      "Lysa Fenn"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "slayWarlords",
        "count": 2
      },
      {
        "type": "rout",
        "count": 210
      }
    ],
    "fail": [],
    "par": {
      "kills": 240,
      "time": 320
    },
    "intro": [
      {
        "who": "GARRICK",
        "line": "Two lords that slipped our grip — the Cinder-Crown and the Warden. They've rallied on the fen."
      },
      {
        "who": "BENJEN",
        "line": "Then no more slipping. Both fall today, and their banners with them."
      }
    ],
    "win": "Vurmog and Draxa — two banners burned in one day. The Dynasty's officer line is gutted.",
    "lose": "The two hold the fen and their heads. The Magmar close ranks and press on.",
    "script": [
      {
        "t": 6,
        "say": {
          "who": "GARRICK",
          "line": "The Cinder-Crown and the Warden, shoulder to shoulder. Both slipped our nooses once."
        }
      },
      {
        "t": 8,
        "ping": {
          "at": "around",
          "tone": "bad"
        }
      },
      {
        "kills": 40,
        "say": {
          "who": "MAGMAR",
          "line": "You should have burned us when you had the chance, Daguth."
        }
      },
      {
        "t": 90,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 28,
          "at": "north"
        }
      },
      {
        "kills": 150,
        "say": {
          "who": "BENJEN",
          "line": "One banner down. Run the last lord to ground."
        }
      },
      {
        "t": 180,
        "announce": {
          "text": "THE OFFICER LINE IS BREAKING",
          "tone": "good"
        }
      }
    ]
  },
  {
    "id": "l9",
    "act": "ACT II — THE MARCHES OF FIRE",
    "name": "No Ground Left",
    "subtitle": "Take every hold in the bog — while the dead you burned rise against you.",
    "biome": "duskmarsh",
    "kind": "siege",
    "mods": {
      "enemyHp": 1.32,
      "enemyDmg": 1.2,
      "spawn": 1.28,
      "magmarNear": 264,
      "allyNear": 127
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "morale": 42,
    "warlords": [
      "Ashlord Kaevun"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Sera Volk",
      "Bram Dagor"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "captureAll"
      }
    ],
    "fail": [],
    "par": {
      "kills": 230,
      "time": 360
    },
    "intro": [
      {
        "who": "BRAM",
        "line": "Every bastion on the field, Commander. Take them all and the citadel stands alone."
      },
      {
        "who": "MAGMAR",
        "line": "Ashlord Kaevun holds this bog, ashling. You will not have all of it."
      },
      {
        "who": "BENJEN",
        "line": "Not one banner stays red by nightfall, Kaevun. Forward."
      }
    ],
    "win": "Every banner flies black and silver. But the dead we burned are walking — and the road ahead runs to the fire that raised them.",
    "lose": "The Magmar cling to their holds. The march drowns in the sick bog.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "BRAM",
          "line": "Every banner, Commander. Leave them no stone to stand on."
        }
      },
      {
        "kills": 30,
        "say": {
          "who": "MAGMAR",
          "line": "Kaevun holds this ground. You take it only over my dead."
        }
      },
      {
        "t": 70,
        "warlord": "Ysra Coalheart",
        "say": {
          "who": "MAGMAR",
          "line": "You burned me on the coast, Daguth. The Forge lit me again. Nothing you kill stays dead."
        }
      },
      {
        "t": 74,
        "say": {
          "who": "SERA",
          "line": "I saw her fall on the salt — I SAW it. What is that thing?"
        }
      },
      {
        "t": 120,
        "announce": {
          "text": "THE MAGMAR HOLDS ARE FALLING",
          "tone": "good"
        }
      },
      {
        "t": 150,
        "say": {
          "who": "BENJEN",
          "line": "One banner left. Tear it down — and put the Widow back in the mud."
        }
      }
    ]
  },
  {
    "id": "l10",
    "act": "ACT II — THE MARCHES OF FIRE",
    "name": "The Ninefold Charge",
    "subtitle": "Wave on wave pours down the pass. Stand where you are and hold.",
    "biome": "frostmark",
    "kind": "laststand",
    "mods": {
      "enemyHp": 1.4,
      "enemyDmg": 1.26,
      "spawn": 1.34,
      "magmarNear": 277,
      "allyNear": 125
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "morale": 40,
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane",
      "Old Garrick",
      "Bram Dagor"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "survive",
        "time": 150
      },
      {
        "type": "rout",
        "count": 200
      }
    ],
    "fail": [],
    "par": {
      "kills": 260,
      "time": 230
    },
    "intro": [
      {
        "who": "GARRICK",
        "line": "They're throwing everything down the pass at us, Commander. Wave on wave, and no end to them."
      },
      {
        "who": "BENJEN",
        "line": "Then we stand where we are and let the snow bury them. Hold the line — all of it."
      }
    ],
    "win": "The line held through the last charge. Even the Ninefold Ruin broke upon it.",
    "lose": "The waves never stopped. The line lies buried under fire and snow.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "GARRICK",
          "line": "They keep coming out of the white. Dig in, lads — we hold."
        }
      },
      {
        "t": 30,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 24,
          "at": "north"
        }
      },
      {
        "t": 45,
        "announce": {
          "text": "THE SECOND WAVE BREAKS THROUGH",
          "tone": "bad"
        }
      },
      {
        "t": 70,
        "spawn": {
          "team": "magmar",
          "type": "archer",
          "count": 20,
          "at": "flank"
        }
      },
      {
        "t": 100,
        "say": {
          "who": "BRAM",
          "line": "Shields won't hold another push — where is our relief?!"
        }
      },
      {
        "t": 120,
        "warlord": "Skarn Duraghast",
        "say": {
          "who": "MAGMAR",
          "line": "Ninefold Ruin comes for the tenth host. Stand, little line, and be broken."
        }
      },
      {
        "t": 150,
        "announce": {
          "text": "THE LINE HELD",
          "tone": "good"
        }
      }
    ]
  },
  {
    "id": "l11",
    "act": "ACT III — THE UNDYING FORGE",
    "name": "Ninefold Ruin",
    "subtitle": "Skarn Duraghast bars the highland road. This is a reckoning.",
    "biome": "ironpeak",
    "kind": "ambush",
    "mods": {
      "enemyHp": 1.48,
      "enemyDmg": 1.32,
      "spawn": 1.4,
      "magmarNear": 290,
      "allyNear": 123
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "morale": 42,
    "warlords": [
      "Skarn Duraghast"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Bram Dagor",
      "Coran Vael"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "slayWarlord",
        "name": "Skarn Duraghast"
      },
      {
        "type": "rout",
        "count": 180
      }
    ],
    "fail": [],
    "par": {
      "kills": 240,
      "time": 300
    },
    "intro": [
      {
        "who": "BRAM",
        "line": "Skarn Duraghast — Ninefold Ruin — waits on the highland road. He has come for you, Commander."
      },
      {
        "who": "MAGMAR",
        "line": "Nine hosts I have broken, Daguth. I will make yours the tenth skull on my banner-pole."
      },
      {
        "who": "BENJEN",
        "line": "Nine hosts. And not one of them carried Masamune. Come down off that rock and die."
      }
    ],
    "win": "The Ninefold Ruin is ended. Let the Dynasty count it the tenth — and the last of him.",
    "lose": "Skarn stands over the tenth host. Over yours.",
    "script": [
      {
        "t": 4,
        "announce": {
          "text": "SKARN DURAGHAST BARS THE HIGHLAND ROAD",
          "tone": "bad"
        }
      },
      {
        "t": 5,
        "say": {
          "who": "BRAM",
          "line": "The Ninefold Ruin himself. This is the reckoning, Commander."
        }
      },
      {
        "t": 6,
        "ping": {
          "at": "center",
          "tone": "bad"
        }
      },
      {
        "kills": 60,
        "say": {
          "who": "MAGMAR",
          "line": "Nine hosts I broke on this maul. Yours makes ten."
        }
      },
      {
        "t": 80,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 26,
          "at": "around"
        }
      },
      {
        "kills": 150,
        "say": {
          "who": "BENJEN",
          "line": "Nine hosts. Not one of them carried Masamune."
        }
      }
    ]
  },
  {
    "id": "l12",
    "act": "ACT III — THE UNDYING FORGE",
    "name": "The Gates of Magmar",
    "subtitle": "Take the last two holds and break the Breaker guarding the doors.",
    "biome": "ashlands",
    "kind": "siege",
    "mods": {
      "enemyHp": 1.56,
      "enemyDmg": 1.38,
      "spawn": 1.47,
      "magmarNear": 303,
      "allyNear": 121
    },
    "bastionsMagmar": [
      0,
      1
    ],
    "warlords": [
      "Ashlord Kaevun"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Coran Vael",
      "Mother Isolde"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "captureAll"
      },
      {
        "type": "slayWarlord",
        "name": "Ashlord Kaevun"
      }
    ],
    "fail": [],
    "par": {
      "kills": 260,
      "time": 340
    },
    "intro": [
      {
        "who": "HALDRIC",
        "line": "Two gates, and the Breaker of the Ninth Host between them. Take the holds — I'll break the doors."
      },
      {
        "who": "MAGMAR",
        "line": "You broke Skarn. You will not break me, ashling. Kaevun does not fall."
      },
      {
        "who": "BENJEN",
        "line": "Everything falls, Kaevun. We'll see which of us first."
      }
    ],
    "win": "The gates of Magmar buckle and gape. Kaevun lies broken beneath his own doors. Beyond them — the Forge.",
    "lose": "Kaevun holds the gates. The Undying Forge stays sealed.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "HALDRIC",
          "line": "Two holds and the Breaker between them. Take the ground — I'll break the doors."
        }
      },
      {
        "kills": 30,
        "say": {
          "who": "MAGMAR",
          "line": "Ashlord Kaevun, Breaker of the Ninth Host. You will break upon these gates."
        }
      },
      {
        "t": 60,
        "spawn": {
          "team": "magmar",
          "type": "captain",
          "count": 2,
          "at": "around"
        }
      },
      {
        "t": 100,
        "announce": {
          "text": "THE FIRST GATE BUCKLES",
          "tone": "good"
        }
      },
      {
        "t": 140,
        "say": {
          "who": "BENJEN",
          "line": "We will see who breaks, Kaevun."
        }
      },
      {
        "t": 170,
        "announce": {
          "text": "THE GATES OF MAGMAR STAND OPEN",
          "tone": "good"
        }
      }
    ],
    "arena": "citadel_magmar"
  },
  {
    "id": "l13",
    "act": "ACT III — THE UNDYING FORGE",
    "name": "The Long Ash Road",
    "subtitle": "Cut through the last host and reach the inner keep.",
    "biome": "ironpeak",
    "kind": "gauntlet",
    "mods": {
      "enemyHp": 1.63,
      "enemyDmg": 1.44,
      "spawn": 1.53,
      "magmarNear": 317,
      "allyNear": 118
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "start": {
      "x": 0,
      "z": 96,
      "heading": 3.14159
    },
    "morale": 48,
    "warlords": [],
    "allyGenerals": [
      "Haldric Vane",
      "Bram Dagor",
      "Mother Isolde"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "reach",
        "x": 0,
        "z": -120,
        "r": 16,
        "label": "Reach the citadel keep"
      },
      {
        "type": "rout",
        "count": 260
      }
    ],
    "fail": [],
    "par": {
      "kills": 270,
      "time": 360
    },
    "intro": [
      {
        "who": "HALDRIC",
        "line": "One last road of ash to the inner keep. Cut it open, Commander — we are right behind you."
      },
      {
        "who": "BENJEN",
        "line": "One last road. Then it ends where it began — with the one who lit the fire."
      }
    ],
    "win": "The keep doors stand open. Beyond them waits Ourang-Vael himself.",
    "lose": "The last host holds the road. The Forge burns on, sealed.",
    "script": [
      {
        "t": 5,
        "say": {
          "who": "HALDRIC",
          "line": "One last road of ash to the keep. Cut it — we are right behind you."
        }
      },
      {
        "t": 6,
        "ping": {
          "at": "north",
          "tone": "neutral"
        }
      },
      {
        "t": 40,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 26,
          "at": "around"
        }
      },
      {
        "t": 60,
        "say": {
          "who": "MAGMAR",
          "line": "The Forge does not cool, black sword. It only waits for you."
        }
      },
      {
        "t": 90,
        "say": {
          "who": "ISOLDE",
          "line": "The Grave-Warden has buried too many on this road. No more — forward, Commander."
        }
      },
      {
        "t": 110,
        "spawn": {
          "team": "magmar",
          "type": "archer",
          "count": 20,
          "at": "flank"
        }
      },
      {
        "t": 150,
        "announce": {
          "text": "THE KEEP DOORS STAND OPEN",
          "tone": "good"
        }
      }
    ]
  },
  {
    "id": "l14",
    "act": "ACT III — THE UNDYING FORGE",
    "name": "Ourang-Vael, the Undying Forge",
    "subtitle": "The Dynast of Magmar. End the war. End him.",
    "biome": "ashlands",
    "kind": "duel",
    "mods": {
      "enemyHp": 1.7,
      "enemyDmg": 1.5,
      "spawn": 1.6,
      "magmarNear": 330,
      "allyNear": 116
    },
    "bastionsMagmar": [
      0,
      1,
      2,
      3
    ],
    "dynast": true,
    "morale": 50,
    "warlords": [
      "Ashlord Kaevun",
      "Skarn Duraghast"
    ],
    "allyGenerals": [
      "Haldric Vane",
      "Sera Volk",
      "Bram Dagor"
    ],
    "warmaster": "Haldric Vane",
    "objectives": [
      {
        "type": "slayDynast"
      }
    ],
    "fail": [
      {
        "type": "protect",
        "name": "Haldric Vane"
      }
    ],
    "par": {
      "kills": 300,
      "time": 420
    },
    "intro": [
      {
        "who": "MAGMAR",
        "line": "I am the Forge that does not cool, Daguth. I was old when your black sword was ore."
      },
      {
        "who": "HALDRIC",
        "line": "Every fire goes out, Commander. Even this one."
      },
      {
        "who": "BENJEN",
        "line": "Then I'll be the dark that takes it. For all of them, Ourang-Vael — this ends now."
      }
    ],
    "win": "THE FORGE IS BROKEN. Ourang-Vael lies dead beneath his citadel, and the dead he raised lie still at last. The Magmar Dynasty burns to cold ash.",
    "lose": "The Undying Forge endures. The Ashlands fall to fire, and the black sword with them.",
    "script": [
      {
        "t": 4,
        "announce": {
          "text": "OURANG-VAEL, THE UNDYING FORGE",
          "tone": "bad"
        }
      },
      {
        "t": 5,
        "say": {
          "who": "MAGMAR",
          "line": "My Breaker. My Ruin. I kept them warm for you, Daguth. Nothing you kill stays dead."
        }
      },
      {
        "t": 6,
        "ping": {
          "at": "center",
          "tone": "bad"
        }
      },
      {
        "t": 45,
        "spawn": {
          "team": "magmar",
          "type": "foot",
          "count": 24,
          "at": "around"
        }
      },
      {
        "kills": 80,
        "say": {
          "who": "HALDRIC",
          "line": "Every fire goes out, Commander. Even this one — put it out."
        }
      },
      {
        "t": 120,
        "spawn": {
          "team": "magmar",
          "type": "captain",
          "count": 2,
          "at": "flank"
        }
      },
      {
        "kills": 160,
        "say": {
          "who": "BENJEN",
          "line": "For Redmarch. For the salt and the snow. For all of them — this ends now."
        }
      }
    ],
    "arena": "citadel_magmar"
  }
];

// star rank from the level's par (S fastest/cleanest -> C)
export function rankFor(level, kills, timeSec) {
  const par = level.par || { kills: 150, time: 300 };
  const timeScore = par.time / Math.max(1, timeSec);
  const killScore = kills / Math.max(1, par.kills);
  const s = timeScore * 0.6 + killScore * 0.4;
  if (s >= 1.35) return 'S';
  if (s >= 1.05) return 'A';
  if (s >= 0.8) return 'B';
  return 'C';
}
